/**
 * PayPalProvider - Implémentation du provider PayPal
 *
 * ⚠️ IMPORTANT: PayPal a déprécié le flow côté client en 2024.
 * Toute création/capture d'ordre doit se faire côté serveur pour la sécurité.
 *
 * Documentation: https://developer.paypal.com/docs/checkout/standard/customize/single-page-app/
 */

import { loadScript } from '@paypal/paypal-js';
import { BasePaymentProvider } from './BaseProvider';

export class PayPalProvider extends BasePaymentProvider {
  constructor(config) {
    super({
      id: 'paypal',
      name: 'PayPal',
      icon: '/icons/payment/paypal.svg',
      ...config
    });

    this.clientId = config.clientId;
    this.paypal = null;
    this.currency = config.currency || 'USD';
    this.intent = config.intent || 'capture';
  }

  /**
   * Initialise PayPal SDK
   * @returns {Promise<any>} Instance PayPal
   */
  async initialize() {
    if (!this.clientId) {
      throw new Error('PayPal Client ID is required');
    }

    try {
      // Charger PayPal SDK avec configuration
      this.paypal = await loadScript({
        'client-id': this.clientId,
        currency: this.currency,
        intent: this.intent,
        'disable-funding': 'credit,card' // Optionnel: désactiver certains moyens de financement
      });

      if (!this.paypal) {
        throw new Error('Failed to load PayPal SDK');
      }

      return this.paypal;
    } catch (error) {
      throw new Error(`PayPal initialization failed: ${error.message}`);
    }
  }

  /**
   * NON UTILISÉ: Les ordres PayPal sont créés côté serveur pour la sécurité
   * Cette méthode existe seulement pour l'interface BaseProvider
   * @param {number} amount - Montant
   * @param {string} currency - Devise
   * @param {object} metadata - Métadonnées
   * @returns {object} Données formatées
   */
  async createPayment(amount, currency, metadata) {
    // Pour PayPal, la création d'ordre se fait via renderButtons (côté serveur)
    return { amount, currency: currency.toLowerCase(), metadata };
  }

  /**
   * NON UTILISÉ: PayPal gère son propre flow via renderButtons
   * @param {object} paymentData - Données (non utilisé)
   * @returns {Promise<void>}
   */
  async processPayment(paymentData) {
    // PayPal gère le flux via les callbacks dans renderButtons
    // Cette méthode existe pour l'interface mais ne fait rien
    return Promise.resolve();
  }

  /**
   * Monte les boutons PayPal avec flow serveur (PRODUCTION-READY 2025)
   *
   * ⚠️ IMPORTANT: createOrder et onApprove font des appels API serveur
   * pour la sécurité. N'utilisez jamais actions.order.create() ou actions.order.capture().
   *
   * @param {string} containerId - ID du conteneur DOM
   * @param {object} options - Options de configuration
   * @param {string} options.createOrderUrl - URL de l'API create-order
   * @param {string} options.captureOrderUrl - URL de l'API capture-order
   * @param {Function} options.onSuccess - Callback en cas de succès
   * @param {Function} options.onError - Callback en cas d'erreur
   * @param {Function} options.onCancel - Callback en cas d'annulation
   * @param {number} options.amount - Montant en cents
   * @param {string} options.currency - Devise
   * @param {object} options.metadata - Métadonnées
   * @returns {Promise<object>} Boutons PayPal montés
   */
  renderButtons(containerId, options = {}) {
    if (!this.paypal) {
      throw new Error('PayPal SDK not initialized. Call initialize() first.');
    }

    const {
      createOrderUrl = '/api/payment/paypal/create-order',
      captureOrderUrl = '/api/payment/paypal/capture-order',
      onSuccess,
      onError,
      onCancel,
      amount,
      currency,
      metadata = {}
    } = options;

    return this.paypal.Buttons({
      // Appeler le serveur pour créer l'ordre (sécurité)
      createOrder: async (data, actions) => {
        try {
          const response = await fetch(createOrderUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              amount: amount / 100, // Convert cents to decimal number
              currency: currency.toLowerCase(),
              metadata: {
                ...metadata,
                orderId: metadata.orderId || `ORD-${Date.now()}`
              }
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create PayPal order');
          }

          const orderData = await response.json();
          return orderData.id; // Retourner l'ID d'ordre PayPal
        } catch (error) {
          console.error('PayPal order creation failed:', error);
          onError?.(error);
          throw error;
        }
      },

      // Appeler le serveur pour capturer le paiement (sécurité)
      onApprove: async (data, actions) => {
        try {
          const { orderID } = data;

          const response = await fetch(captureOrderUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              orderId: orderID,
              metadata: {
                ...metadata,
                payerId: data.payerID,
                paymentId: data.paymentID || data.orderID
              }
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to capture PayPal payment');
          }

          const captureData = await response.json();

          // Valider que le montant capturé correspond au montant attendu
          const capturedAmount = parseFloat(
            captureData.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value
          );
          const expectedAmount = amount / 100; // Convert cents to decimal

          if (Math.abs(capturedAmount - expectedAmount) > 0.01) {
            throw new Error(`Amount mismatch: expected ${expectedAmount}, got ${capturedAmount}`);
          }

          onSuccess?.(captureData);
          return captureData;
        } catch (error) {
          console.error('PayPal capture failed:', error);
          onError?.(error);
          throw error;
        }
      },

      // Gérer l'annulation utilisateur
      onCancel: (data) => {
        console.log('PayPal payment cancelled:', data);
        onCancel?.(data);
      },

      // Gérer les erreurs
      onError: (err) => {
        console.error('PayPal button error:', err);
        onError?.(err);
      }
    }).render(`#${containerId}`);
  }

  /**
   * Formate le montant pour l'API PayPal
   * PayPal nécessite le format décimal (ex: "10.00"), pas les cents
   * @param {number} amountInCents - Montant en cents
   * @returns {string} Montant au format décimal
   */
  formatAmountForPayPal(amountInCents) {
    return (amountInCents / 100).toFixed(2);
  }

  /**
   * Formate le montant depuis la devise locale vers une devise supportée
   * PayPal ne supporte pas XOF/FCFA, donc on convertit en USD/EUR
   * @param {number} amount - Montant original en cents
   * @param {string} fromCurrency - Devise source
   * @returns {number} Montant converti en cents
   */
  formatAmount(amount, fromCurrency) {
    // Devises supportées par PayPal (2025)
    const SUPPORTED_CURRENCIES = [
      'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NOK',
      'SEK', 'DKK', 'PLN', 'HUF', 'CZK', 'ILS', 'MXN', 'BRL', 'PHP'
    ];

    // Si la devise est supportée, retourner tel quel (en cents pour compatibilité)
    if (SUPPORTED_CURRENCIES.includes(fromCurrency.toUpperCase())) {
      return amount;
    }

    // Convertir les devises non supportées (XOF, FCFA) en USD
    const CONVERSION_RATES = {
      'XOF': { to: 'USD', rate: 0.0016 },
      'FCFA': { to: 'USD', rate: 0.0016 }
    };

    const rate = CONVERSION_RATES[fromCurrency.toUpperCase()]?.rate || 0.0016;
    return Math.round(amount * rate);
  }

  /**
   * Vérifie si une devise est supportée par PayPal
   * @param {string} currency - Code devise
   * @returns {boolean} True si supportée
   */
  isCurrencySupported(currency) {
    const SUPPORTED_CURRENCIES = [
      'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NOK',
      'SEK', 'DKK', 'PLN', 'HUF', 'CZK', 'ILS', 'MXN', 'BRL', 'PHP'
    ];
    return SUPPORTED_CURRENCIES.includes(currency.toUpperCase());
  }

  /**
   * Nettoie les ressources PayPal
   */
  destroy() {
    if (this.paypal) {
      this.paypal = null;
    }
  }
}
